"use client"

import {
  FolderIcon,
  MoreHorizontalIcon,
  ShareIcon,
  type LucideIcon,
} from "lucide-react"
import { Link } from "react-router-dom"
import { useComingSoon } from "./coming-soon-modal"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"

export function NavDocuments({
  items,
  currentPath,
}: {
  items: {
    name: string
    url: string
    icon: LucideIcon
  }[]
  currentPath?: string
}) {
  const { isMobile } = useSidebar()
  const { showComingSoon } = useComingSoon()

  const isActive = (url: string) => {
    if (!currentPath) return false;
    if (url === '/reports' && (currentPath === '/reports' || currentPath === '/')) {
      return true;
    }
    return currentPath === url;
  };

  const handleItemClick = (item: { name: string; url: string; icon: LucideIcon }) => {
    if (item.url.startsWith('/')) return; 

    const descriptions: { [key: string]: string } = {
      'Ingredient Library': 'Browse our comprehensive database of ingredients with detailed nutritional profiles, sourcing information, and compatibility data.',
      'Documentation': 'Access detailed guides, API documentation, tutorials, and best practices for using SpyGlass effectively.'
    };

    showComingSoon(item.name, descriptions[item.name] || 'This feature is under development.');
  };

  return (
    <SidebarGroup className="group-data-[collapsible=icon]:hidden">
      <SidebarGroupLabel>Documents</SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => (
          <SidebarMenuItem key={item.name}>
            {item.url.startsWith('/') ? (
              <Link to={item.url} className="w-full no-underline">
                <SidebarMenuButton
                  className={`w-full justify-start cursor-pointer hover:bg-accent hover:text-accent-foreground ${
                    isActive(item.url) ? 'bg-accent text-accent-foreground border-l-2 border-primary' : ''
                  }`}
                >
                  <item.icon />
                  <span>{item.name}</span>
                </SidebarMenuButton>
              </Link>
            ) : (
              <SidebarMenuButton 
                onClick={() => handleItemClick(item)}
                className={`w-full justify-start cursor-pointer hover:bg-accent hover:text-accent-foreground ${
                  isActive(item.url) ? 'bg-accent text-accent-foreground border-l-2 border-primary' : ''
                }`}
              >
                <item.icon />
                <span>{item.name}</span>
              </SidebarMenuButton>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuAction
                  showOnHover
                  className="rounded-sm data-[state=open]:bg-accent"
                >
                  <MoreHorizontalIcon />
                  <span className="sr-only">More</span>
                </SidebarMenuAction>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-24 rounded-lg"
                side={isMobile ? "bottom" : "right"}
                align={isMobile ? "end" : "start"}
              >
                <DropdownMenuItem>
                  <FolderIcon />
                  <span>Open</span>
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <ShareIcon />
                  <span>Share</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        ))}
        <SidebarMenuItem>
          <SidebarMenuButton 
            className="text-sidebar-foreground/70 cursor-pointer hover:bg-accent hover:text-accent-foreground"
            onClick={() => showComingSoon('More Documents', 'Access additional document categories, templates, and collaborative features for enhanced workflow management.')}
          >
            <MoreHorizontalIcon className="text-sidebar-foreground/70" />
            <span>More</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarGroup>
  )
}
